import { useState, useRef, useEffect } from 'react';
import { cn } from '../../lib/utils';
import { ChevronDownIcon } from '../Icons/svgIcons';

const CustomDropdown = ({ 
  options = [], 
  value, 
  onChange, 
  placeholder = 'Select an option', 
  className = '', 
  id, 
  name, 
  disabled = false,
  multiple = false 
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const generatedId = useRef(id || `dropdown-${Math.random().toString(36).substr(2, 9)}`);

  useEffect(() => {
    const handleClickOutside = e => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = optionValue => {
    if (multiple) {
      const currentValues = Array.isArray(value) ? value : [];
      const newValues = currentValues.includes(optionValue)
        ? currentValues.filter(v => v !== optionValue)
        : [...currentValues, optionValue];
      onChange(newValues);
    } else {
      onChange(optionValue);
      setIsOpen(false);
    }
  };

  const safeOptions = Array.isArray(options) ? options : [];
  
  const getDisplayLabel = () => {
    if (multiple) {
      const selectedValues = Array.isArray(value) ? value : [];
      if (selectedValues.length === 0) return placeholder;
      if (selectedValues.length === 1) {
        const option = safeOptions.find(opt => opt.value === selectedValues[0]);
        return option ? option.label : placeholder;
      }
      return `${selectedValues.length} items selected`;
    } else {
      const selectedOption = safeOptions.find(opt => opt.value === value);
      return selectedOption ? selectedOption.label : placeholder;
    }
  };

  const isSelected = (optionValue) => {
    if (multiple) {
      return Array.isArray(value) && value.includes(optionValue);
    }
    return value === optionValue;
  };

  return (
    <div ref={dropdownRef} className="relative w-full">
      <button
        type="button"
        id={generatedId.current}
        name={name || 'dropdown'}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={cn(
          'relative flex items-center justify-between w-full h-12 px-4 text-left bg-white border rounded-lg outline-none transition-all duration-200',
          isOpen ? 'border-[rgb(var(--primary))] ring-4 ring-[rgb(var(--primary))]/10' : 'border-gray-300 hover:bg-gray-50',
          disabled ? 'opacity-50 cursor-not-allowed bg-gray-50' : 'cursor-pointer',
          className
        )}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        <span className={cn('truncate text-sm font-medium', (multiple ? (value?.length > 0) : value) ? 'text-gray-900' : 'text-gray-400')}>
          {getDisplayLabel()}
        </span>
        <span className={`inline-block ml-2 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}>
          <ChevronDownIcon className={cn("w-4 h-4", isOpen ? "text-[rgb(var(--primary))]" : "text-gray-400")} />
        </span>
      </button>

      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-xl max-h-60 overflow-y-auto scrollbar-hide animate-in fade-in zoom-in-95 duration-100">
          {safeOptions.map(option => {
            const selected = isSelected(option.value);
            return (
              <button
                key={option.value}
                type="button"
                onClick={() => handleSelect(option.value)}
                className={cn(
                  'w-full px-4 py-3 text-left text-sm font-medium transition-colors border-b border-gray-50 last:border-0 flex items-center justify-between',
                  selected 
                    ? 'bg-[rgb(var(--primary))]/5 text-[rgb(var(--primary))]' 
                    : 'text-gray-700 hover:bg-gray-50 active:bg-gray-100'
                )}
              >
                <span>{option.label}</span>
                {multiple && selected && (
                   <svg className="w-4 h-4 text-[rgb(var(--primary))]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                     <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                   </svg>
                )}
              </button>
            );
          })}
          {multiple && safeOptions.length > 0 && (
            <div className="p-2 border-t border-gray-100 sticky bottom-0 bg-white">
               <button 
                 type="button" 
                 onClick={() => { onChange([]); setIsOpen(false); }}
                 className="w-full py-2 text-xs font-bold text-red-500 hover:bg-red-50 rounded-md transition-colors"
               >
                 Clear Selection
               </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CustomDropdown;